<script lang="ts">
  import { page } from '$app/stores';
  import { get } from 'svelte/store';
  import { Toolbar } from 'bits-ui';
  import { Home, Inbox, Settings, Landmark } from 'lucide-svelte';
  import RulesIcon from '$lib/components/RulesIcon.svelte';
  import { hapticLight } from '$lib/haptic.js';
  import { resolveActiveTab } from '$lib/tabRouting.js';
  import { spring } from 'svelte/motion';

  let currentPathname = $state(get(page).url.pathname);
  $effect(() => page.subscribe(p => { currentPathname = p.url.pathname; }));

  function tapFeedback() {
    hapticLight();
  }

  const tabs = [
    { href: '/dashboard', label: 'Home', icon: Home },
    { href: '/rules', label: 'Rules', icon: null },
    { href: '/activity', label: 'Activity', icon: Inbox },
    { href: '/accounts', label: 'Accounts', icon: Landmark },
    // subscriptions hidden — featureFlags.subscriptions = false
    { href: '/settings', label: 'Settings', icon: Settings },
  ] as const;

  let activeIndex = $derived((() => {
    const activeHref = resolveActiveTab(
      currentPathname,
      tabs.map((t) => t.href),
    );
    const idx = tabs.findIndex((t) => t.href === activeHref);
    return idx >= 0 ? idx : 0;
  })());

  // Spring-driven indicator position — gives a natural overshoot on quick tab switches.
  const indicatorX = spring(0, { stiffness: 0.22, damping: 0.72 });
  $effect(() => { indicatorX.set(activeIndex * 100); });
</script>

<nav
  class="fixed bottom-0 left-0 right-0 z-20 overflow-hidden rounded-t-[1.75rem] border-x border-t border-border/60 bg-background/95 shadow-[0_-10px_28px_rgb(15_23_42/0.08)] backdrop-blur-md supports-backdrop-filter:bg-background/88 md:hidden"
  style="padding-bottom: max(0.25rem, env(safe-area-inset-bottom, 0px));"
  aria-label="Main"
>
  <div class="mx-auto h-px max-w-lg bg-border/40" aria-hidden="true"></div>

  <Toolbar.Root orientation="horizontal" class="relative mx-auto flex max-w-lg items-stretch justify-around">
    <!--
      Sliding circle: each tab slot = 20% wide.
      Icon area: starts at pt-3 (12px), height h-10 (40px) → center at 32px.
      Circle: 40px → top = 32 - 20 = 12px = top-3.
    -->
    <span
      class="pointer-events-none absolute top-3 left-0 flex h-10 items-center justify-center"
      style="width: 20%; transform: translateX({$indicatorX}%);"
      aria-hidden="true"
    >
      <span class="block h-10 w-10 rounded-full bg-brand-700 dark:bg-brand-900 shadow-xs"></span>
    </span>

    {#each tabs as tab, i (tab.href)}
      {@const isActive = i === activeIndex}
      <Toolbar.Link
        href={tab.href}
        data-sveltekit-preload-data="tap"
        class="relative z-1 flex min-w-0 flex-1 flex-col items-center gap-0.5 pt-3 pb-1.5 transition-colors duration-200
               {isActive
          ? 'text-white dark:text-mint-300'
          : 'text-muted-foreground hover:text-foreground'}"
        aria-current={isActive ? 'page' : undefined}
        onpointerup={tapFeedback}
      >
        <span class="flex h-10 w-10 items-center justify-center">
          {#if tab.icon}
            {@const TabIcon = tab.icon}
            <TabIcon
              class="relative h-5 w-5"
              strokeWidth={isActive ? 2.25 : 1.75}
              aria-hidden="true"
            />
          {:else}
            <RulesIcon
              class="relative h-5 w-5"
              strokeWidth={isActive ? 2.25 : 1.75}
            />
          {/if}
        </span>
        <span
          class="text-[0.65rem] leading-tight
            {isActive ? 'font-bold text-brand-700 dark:text-mint-400' : 'font-medium'}"
        >{tab.label}</span>
      </Toolbar.Link>
    {/each}
  </Toolbar.Root>
</nav>
